/* eslint-disable prefer-template */
/* eslint-disable max-len */
import { h } from '@stencil/core';
import { FormData, FormField, AnyFormElement, FormFieldDataSet } from '../forms-ui-form-faas.interface';
import { CascadeService } from './cascade-service';
import { isZoomInfoSearchField } from './zoominfo';

export type VariableProps = {
	[key: string]: string | number | boolean
};

export const validateEmailDomain = (target: HTMLFormsUiInputElement, formField: FormField) => {
	/** if no prohibited email domains string in formField object, return */
	if (!formField.prohibitedEmailDomains) return;

	const prohibitedDomains = formField.prohibitedEmailDomains?.split(',');

	/** exit if no prohibited domains */
	if (!prohibitedDomains || prohibitedDomains.length < 1) return;

	/** get ref's email domain */
	const emailDomain = target.value.split('@')[1];

	/** finally, actually perform check and set error status accordingly. */
	if (prohibitedDomains.includes(emailDomain)) {
		target.setError(true, 'Please enter a valid email address', true);
	}
};

// handle ref function (post render)
export const postRenderRefHandler = (ref: AnyFormElement, formFieldData: FormField, returnRefs: Function) => {
	if (ref && formFieldData.validationText) ref.setError(ref.error, formFieldData.validationText);
	returnRefs(ref);
};

export const renderCheckboxComponent = (props: VariableProps, formFieldData: FormField, addRef: Function) => {
	props.value = props.name;
	return <forms-ui-checkbox
		{...props}
		ref={(elem) => postRenderRefHandler(elem, formFieldData, addRef)}
		checked={formFieldData.defaultText === 'true'}
	/>;
};

// eslint-disable-next-line arrow-body-style
export const renderCheckboxGroupComponent = (props: VariableProps, formFieldData: FormField, addRef: Function) => {
	return <forms-ui-checkbox-group {...props} ref={(elem) => postRenderRefHandler(elem, formFieldData, addRef)}>
		{formFieldData.dataSets.map(
			(checkbox) => <forms-ui-checkbox
				key={checkbox.value}
				label={checkbox.label}
				value={checkbox.value}
				checked={formFieldData.defaultText === checkbox.value}>
			</forms-ui-checkbox>,
		)}
	</forms-ui-checkbox-group>;
};

export const renderCalendarComponent = (props: VariableProps, formFieldData: FormField, addRef: Function) => {
	if (formFieldData.additionalInformation) {
		const addtlInfo = formFieldData.additionalInformation;
		if (addtlInfo.fromYear) props.minDate = `01/01/${addtlInfo.fromYear}`;
		if (addtlInfo.throughYear) props.maxDate = `12/31/${addtlInfo.throughYear}`;
		if (formFieldData.placeholderText) props.placeholder = formFieldData.placeholderText;
		if (formFieldData.defaultText) props.value = formFieldData.defaultText;
		if (props.helperText) props.internalHelperText = props.helperText;
	}
	return <forms-ui-date-picker {...props} ref={(elem) => postRenderRefHandler(elem, formFieldData, addRef)} />;
};

export const renderInputComponent = (props: VariableProps, formFieldData: FormField, addRef: Function) => {
	const typeMap = {
		Email: 'email',
		Text: 'text',
		Number: 'number',
		URL: 'url',
		Phone: 'tel',
		Password: 'password',
		Hidden: 'hidden',
	};
	props.type = typeMap[formFieldData.type];

	if (formFieldData.regex) props.pattern = formFieldData.regex;
	if (formFieldData.placeholderText) props.placeholder = formFieldData.placeholderText;
	if (formFieldData.defaultText) props.value = formFieldData.defaultText;

	if (formFieldData.type === 'Number') {
		if (formFieldData.minLength) props.min = formFieldData.minLength;
		if (formFieldData.maxLength) props.max = formFieldData.maxLength;
	} else {
		if (formFieldData.minLength) props.minLength = formFieldData.minLength;
		if (formFieldData.maxLength) props.maxLength = formFieldData.maxLength;
	}
	return <forms-ui-input
		{...props}
		ref={(elem) => postRenderRefHandler(elem, formFieldData, addRef)}
		onFormsUIInputBlur={
			formFieldData.type === 'Email'
				? (e: CustomEvent) => validateEmailDomain(e.target as HTMLFormsUiInputElement, formFieldData)
				: undefined
		}
	/>;
};

// eslint-disable-next-line arrow-body-style
export const renderRadioButtonGroupComponent = (props: VariableProps, formFieldData: FormField, addRef: Function) => {
	return <forms-ui-radio-button-group {...props} ref={(elem) => postRenderRefHandler(elem, formFieldData, addRef)}>
		{formFieldData.dataSets.map(
			(radioButton) => <forms-ui-radio-button
				key={radioButton.value}
				label={radioButton.label}
				value={radioButton.value}
				checked={formFieldData.defaultText === radioButton.value}>
			</forms-ui-radio-button>,
		)}
	</forms-ui-radio-button-group>;
};

export const renderTextareaComponent = (props: VariableProps, formFieldData: FormField, addRef: Function) => {
	if (formFieldData.minLength) props.minLength = formFieldData.minLength;
	if (formFieldData.maxLength) props.maxLength = formFieldData.maxLength;
	if (formFieldData.placeholderText) props.placeholder = formFieldData.placeholderText;
	if (formFieldData.defaultText) props.value = formFieldData.defaultText;
	return <forms-ui-textarea {...props} ref={(elem) => postRenderRefHandler(elem, formFieldData, addRef)} />;
};

export const renderDropdownComponent = (props: VariableProps, formFieldData: FormField, addRef: Function) => {
	if (formFieldData.placeholderText) props.placeholder = formFieldData.placeholderText;
	if (formFieldData.type === 'Hidden') props.hide = true;
	/** initializing the Map for groupedDataSets */
	/* eslint-disable-next-line no-case-declarations */
	// eslint-disable-next-line no-case-declarations
	const groupedDataSets = new Map<string, FormFieldDataSet[]>();
	const hasGroups = formFieldData.groups && formFieldData.groups.length > 0 && formFieldData.dataSets.length > 0;
	if (hasGroups) {
		formFieldData.groups.forEach((group) => {
			groupedDataSets.set(group.name, []);
		});
	}

	formFieldData.dataSets.forEach((dataSet) => {
		if (groupedDataSets.has(dataSet.group)) {
			groupedDataSets.get(dataSet.group).push(dataSet);
		}
	});
	return <forms-ui-select {...props} ref={(elem) => postRenderRefHandler(elem, formFieldData, addRef)}>
		{
			/** Render logic */
			hasGroups
				? (formFieldData.groups.map((group) => (
					<forms-ui-select-option-group key={group.name} label={group.label} name={group.name}>
						{groupedDataSets.get(group.name).map((option) => (
							<forms-ui-select-option
								key={option.value}
								label={option.label}
								value={option.value}
								selected={formFieldData.defaultText === option.value}>
							</forms-ui-select-option>))
						}
					</forms-ui-select-option-group>
				))
				) : formFieldData.dataSets.map(
					(option) => <forms-ui-select-option
						key={option.value}
						label={option.label}
						value={option.value}
						selected={formFieldData.defaultText === option.value}>
					</forms-ui-select-option>,
				)
		}
	</forms-ui-select>;
};

/** Accepts a form field, and returns appropriate component with field data applied */
function renderInternalFormElement(formFieldData: FormField, addRef: Function): AnyFormElement {
	const props: VariableProps = {
		label: formFieldData.label || '',
		name: formFieldData.name || '',
		required: formFieldData.required || false,
		helperText: formFieldData.additionalInformation?.additionalText || '',
		'data-element': formFieldData.dataElement,
	};

	switch (formFieldData.control) {
		case 'Checkbox':
			return renderCheckboxComponent(props, formFieldData, addRef);
		case 'Checkbox Group':
			return renderCheckboxGroupComponent(props, formFieldData, addRef);
		case 'Calendar':
			return renderCalendarComponent(props, formFieldData, addRef);
		case 'Input':
			return renderInputComponent(props, formFieldData, addRef);
		case 'Radio Button Group':
			return renderRadioButtonGroupComponent(props, formFieldData, addRef);
		case 'Textarea':
			return renderTextareaComponent(props, formFieldData, addRef);
		case 'Dropdown':
			return renderDropdownComponent(props, formFieldData, addRef);
		default:
			throw new Error(`Invalid component type specified: '${formFieldData.control}'`);
	}
}
/** wraps form fields in div with corresponding classes for styling and identifying */
export const renderFormElement = (formFieldData: FormField, addRef: Function, key: string): HTMLDivElement => {
	if (formFieldData.type === 'Hidden') {
		return (<div class='forms-ui-form-element hidden' key={key}> {renderInternalFormElement(formFieldData, addRef)}</div>);
	}
	if (formFieldData.control === 'Checkbox') {
		return (<div class='forms-ui-form-element checkbox' key={key}> {renderInternalFormElement(formFieldData, addRef)}</div>);
	}
	return (<div class='forms-ui-form-element' key={key}> {renderInternalFormElement(formFieldData, addRef)}</div>);
};

/**
 * Generate a key to assist in efficient re-rendering of form elements.
 * A special case is provided for the zoomInfo search field because it
 * needs to be re-rendered if the zoomInfo acceptance changes, while other components should be untouched.
 */
export const generateKey = (formField: FormField, zoomReject: boolean) => {
	if (isZoomInfoSearchField(formField)) {
		return `${formField.dataElement}-${zoomReject ? 'rejected' : 'accepted'}`;
	}
	return formField.dataElement;
};

/**
 * Renders the form fields, including handling cascade dropdowns.
 *
 * @param formData - The form data containing all fields to be rendered.
 * @param addFormRef - The callback function to track references to rendered elements.
 * @returns Array of rendered form fields as JSX elements.
 */
export const renderFields = (
	formData: FormData,
	addRef: Function,
	zoomReject: boolean,
): HTMLDivElement[] => formData.formFields.map((formField: FormField) => {
	if (formField.control === 'Dropdown' && formField.cascadeRank === 0) {
		const cascadeService = new CascadeService(
			formData.formFields,
			formField.cascadeGroup,
			addRef,
		);

		// Call renderAllDropdowns to render the cascading dropdowns
		const dropdownElements = cascadeService.renderAllDropdowns();

		if (dropdownElements) {
			return (
				<div class="forms-ui-form-element" key={formField.dataElement}>
					{dropdownElements}
				</div>
			);
		}

		return null;
	}

	// Render non-Cascade for backward compatibility
	if (!formField.cascadeRank && !formField.cascadeGroup) {
		const key = generateKey(formField, zoomReject);
		return renderFormElement(formField, addRef, key);
	}

	// Skip rendering for Cascade elements with cascadeRank > 0
	return null;
});
